import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { startImport } from '../../api/mysqlImport';
import { ImportLogDrawer } from './ImportLogDrawer';
import type { MySQLDatabase } from '../../api/mysql';

interface Props {
  open:     boolean;
  db:       MySQLDatabase | null;
  charsets: Record<string, string[]>;
  onClose:  () => void;
}

export function MySQLImportDialog({ open, db, charsets, onClose }: Props) {
  const [file, setFile]           = useState<File | null>(null);
  const [charset, setCharset]     = useState('utf8mb4');
  const [collation, setCollation] = useState('utf8mb4_0900_ai_ci');
  const [confirmName, setConfirm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [jobId, setJobId]         = useState<string | null>(null);

  const reset = () => {
    setFile(null); setConfirm(''); setError(null);
    setUploading(false); setJobId(null);
    setCharset('utf8mb4'); setCollation('utf8mb4_0900_ai_ci');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    if (!file || !db) return;
    if (confirmName !== db.name) {
      setError('Name does not match. Type the database name exactly.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const result = await startImport(db.name, file, charset, collation);
      setJobId(result.job_id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setError(msg);
      setUploading(false);
    }
  };

  if (jobId && db) {
    return (
      <ImportLogDrawer
        open={open}
        jobId={jobId}
        dbname={db.name}
        onClose={handleClose}
      />
    );
  }

  const collations = charsets[charset] ?? [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Dump — {db?.name}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          The database <strong>{db?.name}</strong> will be dropped and recreated.
          All existing data will be lost.
        </Alert>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* File picker */}
        <Box sx={{ mb: 2 }}>
          <Button variant="outlined" component="label">
            {file ? file.name : 'Select .sql.gz or .sql file'}
            <input
              type="file"
              hidden
              accept=".sql,.gz,.sql.gz"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          {file && (
            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {(file.size / 1024 / 1024).toFixed(1)} MB
            </Typography>
          )}
        </Box>

        {/* Charset */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Character set</InputLabel>
          <Select
            value={charset}
            label="Character set"
            onChange={(e) => {
              setCharset(e.target.value);
              setCollation(charsets[e.target.value]?.[0] ?? '');
            }}
          >
            {Object.keys(charsets).map((cs) => (
              <MenuItem key={cs} value={cs}>{cs}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Collation */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Collation</InputLabel>
          <Select
            value={collation}
            label="Collation"
            onChange={(e) => setCollation(e.target.value)}
            disabled={collations.length === 0}
          >
            {collations.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Confirm name */}
        <TextField
          fullWidth
          size="small"
          label={`Type "${db?.name}" to confirm`}
          value={confirmName}
          onChange={(e) => setConfirm(e.target.value)}
          error={confirmName.length > 0 && confirmName !== db?.name}
          helperText="This will drop and recreate the database. This action cannot be undone."
        />

        {uploading && <LinearProgress sx={{ mt: 2 }} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={!file || confirmName !== db?.name || uploading}
          onClick={() => void handleSubmit()}
        >
          {uploading ? 'Uploading…' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
